import { RequestQueryBuilder } from '@nestjsx/crud-request';
import { ErrorReportingService, HttpService } from 'src/services';
import {
  Challenge,
  ChallengeDto,
  ChallengeId,
  ChallengeSession,
  ChallengeSessionDto,
  ChallengeSessionId,
  ChallengeStepId,
  START_HERE_CHALLENGE_ID,
  ChallengeType,
} from 'src/models/Challenge';
import { ChallengeQuery } from 'src/models/Challenge';
import { extractResponseData, GetManyResponse } from 'src/types';
import { Comment, CommentDto, CommentId } from 'src/models/Comment';
import { composeQuerySearch } from './ChallengesService.helper';

// Full relation set — only for single-entity fetches (getById), where there's no
// join fan-out cost since it's always exactly one root row.
const createBaseChallengeQueryBuilder = () =>
  new RequestQueryBuilder()
    .setJoin({ field: 'coach' })
    .setJoin({ field: 'equipment' })
    .setJoin({ field: 'tag' })
    .setJoin({ field: 'extras' })
    .setJoin({ field: 'video' })
    .setJoin({ field: 'video.thumbnail' })
    .setJoin({ field: 'horizontalThumbnail' })
    .setJoin({ field: 'verticalThumbnail' });

// Listing/library query — cards only render coach + thumbnails, and the search
// filters only ever query by coach.id / tag.id, so equipment/extras are dropped
// to shrink the ManyToMany join fan-out on paginated (getManyAndCount) queries.
const createListChallengeQueryBuilder = () =>
  new RequestQueryBuilder()
    .setJoin({ field: 'coach' })
    .setJoin({ field: 'tag' })
    .setJoin({ field: 'video' })
    .setJoin({ field: 'video.thumbnail' })
    .setJoin({ field: 'horizontalThumbnail' })
    .setJoin({ field: 'verticalThumbnail' });

const createCarouselChallengeQueryBuilder = () =>
  new RequestQueryBuilder()
    .setJoin({ field: 'coach' })
    .setJoin({ field: 'video' })
    .setJoin({ field: 'video.thumbnail' })
    .setJoin({ field: 'horizontalThumbnail' })
    .setJoin({ field: 'verticalThumbnail' });

// Filter-option dropdowns only ever read .tag and .coach off the response, so
// this skips video/thumbnails entirely on top of dropping equipment/extras —
// this is the query that runs with limit=500, where join fan-out hurts most.
const createFilterOptionsQueryBuilder = () =>
  new RequestQueryBuilder()
    .setJoin({ field: 'coach' })
    .setJoin({ field: 'tag' });

const createBaseChallengeSessionQueryBuilder = () =>
  new RequestQueryBuilder()
    .setJoin({ field: 'coach' })
    .setJoin({ field: 'equipment' })
    .setJoin({ field: 'style' })
    .setJoin({ field: 'tag' })
    .setJoin({ field: 'type' })
    .setJoin({ field: 'extras' })
    .setJoin({ field: 'video' })
    .setJoin({ field: 'video.thumbnail' })
    .setJoin({ field: 'horizontalThumbnail' });

const composeQuery = (
  query?: ChallengeQuery,
  challengeType = ChallengeType.Challenge,
) => {
  const qb = createListChallengeQueryBuilder()
    .sortBy({ field: 'updatedDate', order: 'DESC' })
    .setFilter({ field: 'published', operator: '$eq', value: true })
    .setFilter({
      field: 'id',
      operator: '$ne',
      value: START_HERE_CHALLENGE_ID,
    });

  if (query?.limit) {
    qb.setLimit(query.limit);
  }
  if (query?.page) {
    qb.setPage(query.page);
  }

  if (query?.filters) {
    const searchConditions = composeQuerySearch(query.filters, {
      excludeIds: [START_HERE_CHALLENGE_ID],
    });
    qb.search(searchConditions);
  }

  const type = query?.challengeType ?? challengeType;
  return `type=${type}&${qb.query()}`;
};

const composeCarouselQuery = (limit: number) =>
  createCarouselChallengeQueryBuilder()
    .sortBy({ field: 'updatedDate', order: 'DESC' })
    .setFilter({ field: 'published', operator: '$eq', value: true })
    .setLimit(limit)
    .query();

const composeFilterOptionsQuery = (challengeType: ChallengeType, limit: number) =>
  `type=${challengeType}&${createFilterOptionsQueryBuilder()
    .setFilter({ field: 'published', operator: '$eq', value: true })
    .setFilter({
      field: 'id',
      operator: '$ne',
      value: START_HERE_CHALLENGE_ID,
    })
    .setLimit(limit)
    .setPage(1)
    .query()}`;

const ChallengessService = {

  getAll: (): Promise<GetManyResponse<Challenge>> => HttpService
    .get<GetManyResponse<ChallengeDto>>(
      `/public/challenges?published=true&type=${ChallengeType.Challenge}&${createBaseChallengeQueryBuilder().query()}`,
    )
    .then(response => ({
      ...response,
      data: response.data.map(c => Challenge.fromDto(c, { excludeUnpublishedSteps: true }))
    })),

  getMany: (query?: ChallengeQuery): Promise<GetManyResponse<Challenge>> => HttpService
    .get<GetManyResponse<ChallengeDto> | ChallengeDto[]>(`/public/challenges?${composeQuery(query)}`)
    .then(response => {
      const items = extractResponseData(response);
      return Array.isArray(response)
        ? { data: items.map(c => Challenge.fromDto(c, { excludeUnpublishedSteps: true })), count: items.length, total: items.length, page: 1, pageCount: 1 }
        : { ...response, data: items.map(c => Challenge.fromDto(c, { excludeUnpublishedSteps: true })) };
    }),

  getManyForCarousel: (limit: number): Promise<Challenge[]> => HttpService
    .get<GetManyResponse<ChallengeDto> | ChallengeDto[]>(`/public/challenges?${composeCarouselQuery(limit)}`)
    .then(response => extractResponseData(response).map(c => Challenge.fromDto(c, { excludeUnpublishedSteps: true }))),

  getFilterOptions: (challengeType: ChallengeType, limit: number): Promise<Challenge[]> => HttpService
    .get<GetManyResponse<ChallengeDto> | ChallengeDto[]>(`/public/challenges?${composeFilterOptionsQuery(challengeType, limit)}`)
    .then(response => extractResponseData(response).map(c => Challenge.fromDto(c, { excludeUnpublishedSteps: true }))),

  getById: (id: ChallengeId, { excludeUnpublishedSteps = true } = {}): Promise<Challenge> => HttpService
    .get<ChallengeDto>(`/public/challenges/${id}?${createBaseChallengeQueryBuilder().query()}`)
    .then(c => Challenge.fromDto(c, { excludeUnpublishedSteps })),

  getByIdV2: (id: ChallengeId | ChallengeSessionId,
    { excludeUnpublishedSteps = true, bySession }: { excludeUnpublishedSteps?: boolean, bySession?: boolean } = {}): Promise<Challenge> => HttpService
    .get<ChallengeDto>(`/public/challenges/v2/${id}${bySession === true ? '?bySession' : ''}`)
    .then(c => Challenge.fromDto(c, { excludeUnpublishedSteps })),

  getSessionById: async (id: ChallengeSessionId) => HttpService
    .get<ChallengeSessionDto>(
      `/public/challenges/sessions/${id}?${createBaseChallengeSessionQueryBuilder().query()}`
    )
    .then(ChallengeSession.fromDto),

  toggleChallengeLiked: (id: ChallengeId, status = true) => HttpService
    .patch(`/public/challenges/${id}/activity/like?status=${status}`),

  toggleChallengeSessionLiked: (id: ChallengeSessionId, status = true) => HttpService
    .patch(`/public/challenges/sessions/${id}/activity/like?status=${status}`),

  markChallengeVisited: (id: ChallengeId) => HttpService
    .patch(`/public/challenges/${id}/activity/visit?status=true`),

  toggleChallengeStepCompleted: (id: ChallengeStepId, status: boolean) => HttpService
    .patch(`/public/challenges/step/${id}/activity/view?status=${status}`)
    .catch(() => ErrorReportingService.report(`Could not mark challenge as visited; challenge id: ${id}`)),


  getComments: async ({
    challengeId,
    challengeSessionId,
    page,
    limit,
  }: {
    challengeId?: ChallengeId,
    challengeSessionId?: ChallengeSessionId,
    page: number,
    limit: number
  }): Promise<GetManyResponse<Comment>> => {
    const builder = new RequestQueryBuilder();
    if (challengeId) {
      builder.setFilter({ field: 'challenge.id', operator: '$eq', value: challengeId });
    } else if (challengeSessionId) {
      builder.setFilter({ field: 'challengeSession.id', operator: '$eq', value: challengeSessionId });
    } else {
      throw new Error('No challege or challenge session ID was provided');
    }
    const query = builder
      .setPage(page)
      .setLimit(limit)
      .sortBy({ field: 'createdDate', order: 'DESC' })
      .query();
    const response = await HttpService
      .get<GetManyResponse<CommentDto>>(`/public/comment?${query}`);
    return {
      ...response,
      data: response.data.map(Comment.fromDto)
    };
  },

  postChallengeComment: (challengeId: ChallengeId, content: string) => HttpService
    .post<CommentDto>('/public/comment', { challengeId, content })
    .then(Comment.fromDto),

  postChallengeSessionComment: (challengeSessionId: ChallengeSessionId, content: string) => HttpService
    .post<CommentDto>('/public/comment', { challengeSessionId, content })
    .then(Comment.fromDto),

  removeComment: (commentId: CommentId) => HttpService
    .delete(`/public/comment/${commentId}`),

  editComment: (commentId: CommentId, content: string) => HttpService
    .patch<CommentDto>(`/public/comment/${commentId}`, { content })
    .then(Comment.fromDto),

  hideComment: (commentId: CommentId) => HttpService
    .patch<CommentDto>(`/comment/${commentId}`, { hidden: true })
    .then(Comment.fromDto),

  unhideComment: (commentId: CommentId) => HttpService
    .patch<CommentDto>(`/comment/${commentId}`, { hidden: false })
    .then(Comment.fromDto),
};

export default ChallengessService;
